import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  RestartAlt as ResetIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { GridColumnState } from './grid.types';

type Props = {
  columns: GridColumnState[];
  onVisibilityChange: (key: string, visible: boolean) => void;
  onWidthChange: (key: string, width: number) => void;
  onMove: (key: string, direction: 'up' | 'down') => void;
  onReset: () => void;
  onResetToAPI?: () => Promise<void>;
  onSave?: () => void;
};

export default function ColumnCustomizer({
  columns,
  onVisibilityChange,
  onWidthChange,
  onMove,
  onReset,
  onResetToAPI,
  onSave,
}: Props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="Customize Columns">
        <IconButton size="small" sx={{marginRight: "20px"}} onClick={() => setOpen(true)}>
          <ViewColumnIcon sx={{ fontSize: '1.7rem' }} color='primary' />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ disableScrollLock: true }}
        PaperProps={{
          sx: {
            width: 320,
            p: 2,
            top: 74,
            height: 'calc(100vh - 74px - 32px)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Customize Columns
          </Typography>
          <Tooltip title="Reset to default">
            <IconButton size="small" onClick={onReset}>
              <ResetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {columns.map((col, idx) => (
            <Box
              key={col.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {/* Visibility toggle */}
              <FormControlLabel
                sx={{ flex: 1, m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={col.visible}
                    onChange={(e) =>
                      onVisibilityChange(col.key, e.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant="body2" noWrap>
                    {col.label}
                  </Typography>
                }
              />


              {/* Reorder buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <IconButton
                  size="small"
                  disabled={idx === 0}
                  onClick={() => onMove(col.key, 'up')}
                  sx={{ p: 0.25 }}
                >
                  <ArrowUpIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={idx === columns.length - 1}
                  onClick={() => onMove(col.key, 'down')}
                  sx={{ p: 0.25 }}
                >
                  <ArrowDownIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>

        {onSave && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              size="small"
              onClick={() => {
                onSave();
                setOpen(false);
              }}
            >
              Save Preference
            </Button>
            {onResetToAPI && (
              <Button
                variant="outlined"
                fullWidth
                size="small"
                onClick={() => {
                  onResetToAPI();
                  setOpen(false);
                }}
              >
                Restore to Defaults
              </Button>
            )}
          </Box>
        )}
      </Drawer>
    </>
  );
}
